/**
 * L'IDENTITÀ DELLA PIATTAFORMA — nome, chi la gestisce, contatti (26-10-2026, task 2061).
 *
 * Arriva da `GET /api/public/platform-identity`, che risponde **senza accesso**: serve alla pagina
 * di login, all'aiuto, a «Informazioni» e alle segnalazioni. I valori li compila il superadmin in
 * «Sistema» (riga `Setup` web); `operatorName` è il nome da mostrare dell'azienda principale.
 *
 * ⚠️ Un campo vuoto arriva `null` e vuol dire «non mostrarlo». Ma finché il server non ha la rotta
 * (prima del rilascio) o il superadmin non ha compilato i contatti, togliere i contatti lascerebbe
 * le persone senza un modo di chiedere aiuto: per quei casi ci sono i valori di ripiego qui sotto,
 * che sono quelli scritti nelle pagine fino a oggi (vedi `docs/BE-RESPONSE-TO-FE-NOME-DA-MOSTRARE-v1.md` §2).
 */
import { useQuery } from '@tanstack/react-query';
import { httpRequest } from '@/shared/api/client';

export interface PlatformIdentity {
  applicationName: string | null;
  operatorName: string | null;
  supportEmail: string | null;
  statusUrl: string | null;
  docsUrl: string | null;
  websiteUrl: string | null;
  privacyEmail: string | null;
  copyright: string | null;
}

/** I valori di oggi: valgono finché il server non ne dà di suoi. */
export const PLATFORM_FALLBACK: PlatformIdentity = {
  applicationName: null,
  operatorName: null,
  supportEmail: 'supporto@maestrale.it',
  statusUrl: 'https://status.maestrale.it',
  docsUrl: 'https://docs.maestrale.it/maefwk10',
  websiteUrl: 'https://maestrale.it',
  privacyEmail: 'privacy@maestrale.it',
  copyright: null,
};

const clean = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * Unisce la risposta ai ripieghi: un campo del server vince, uno vuoto ripiega. Il ripiego cade
 * appena il superadmin compila il campo — da quel momento vale il suo, anche per i contatti.
 */
export function mergeIdentity(server: Partial<Record<keyof PlatformIdentity, unknown>> | null | undefined): PlatformIdentity {
  const out = { ...PLATFORM_FALLBACK };
  for (const k of Object.keys(out) as (keyof PlatformIdentity)[]) {
    const v = clean(server?.[k]);
    if (v) out[k] = v;
  }
  return out;
}

export async function fetchPlatformIdentity(): Promise<PlatformIdentity> {
  try {
    const res = await httpRequest<Partial<PlatformIdentity> | { data?: Partial<PlatformIdentity> }>(
      '/api/public/platform-identity', { method: 'GET', endpointType: 'plain', anonymous: true, retries: 0 },
    );
    const body = res && typeof res === 'object' && 'data' in res && res.data ? res.data : res;
    return mergeIdentity(body as Partial<PlatformIdentity>);
  } catch {
    /* Prima del rilascio la rotta non c'è (404): si resta sui valori di oggi, senza errori a schermo. */
    return PLATFORM_FALLBACK;
  }
}

export function usePlatformIdentity(): PlatformIdentity {
  const q = useQuery({ queryKey: ['platform-identity'], staleTime: 60 * 60_000, queryFn: fetchPlatformIdentity });
  return q.data ?? PLATFORM_FALLBACK;
}

/** «a chi gestisce la piattaforma», oppure «a Gruppo Rossi» quando il nome c'è. */
export const operatorPhrase = (p: PlatformIdentity, prep: 'a' | 'di' = 'a'): string =>
  (p.operatorName ? `${prep} ${p.operatorName}` : `${prep} chi gestisce la piattaforma`);

/**
 * Riscrive su `p` i collegamenti scritti coi valori di ripiego (guida, mail del supporto): serve agli
 * elenchi fissi come i collegamenti rapidi dell'aiuto, che restano costanti e si adattano qui.
 */
export function withPlatformLinks<T extends { href?: string; description: string }>(links: readonly T[], p: PlatformIdentity): T[] {
  const swap = (s: string) => s
    .split(PLATFORM_FALLBACK.supportEmail!).join(p.supportEmail ?? '')
    .split(PLATFORM_FALLBACK.docsUrl!).join(p.docsUrl ?? '');
  return links.map((l) => ({ ...l, description: swap(l.description), ...(l.href ? { href: swap(l.href) } : {}) }));
}
